"use client"

import { useState } from "react"
import { z } from "zod"

import { pro } from "@productlane/lib/src/browser"
import {
	AlertDialog,
	Badge,
	Button,
	Form,
	FormInput,
	IntegrationTemplate,
	Loader,
	PlainLogo,
	SubmitButton,
	useZodForm,
} from "@productlane/ui"

import type { RouterOutputs } from "@/lib/api"
import { toast } from "@/components/toast"
import { UpgradeButton } from "@/components/upgrade-button"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useRequiredPlans } from "@/lib/plans"
import { IntegrationLayout } from "./integration-layout"

export function PlainIntegrationsPage() {
	const workspaceQuery = api.workspace.info.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
	})
	return (
		<>
			{workspaceQuery.data ? (
				<PlainForm data={{ workspace: workspaceQuery.data }} />
			) : (
				<div className="flex justify-center">
					<Loader />
				</div>
			)}
		</>
	)
}

interface PlainFormProps {
	data: {
		workspace: RouterOutputs["workspace"]["info"]
	}
}

const schema = z.object({
	token: z.string().min(1),
})

function PlainForm({ data }: PlainFormProps) {
	const ctx = api.useUtils()
	const disconnectMutation = api.workspace.disconnectPlain.useMutation({
		onSuccess: async () => {
			await ctx.workspace.info.invalidate()
			toast.success({ title: "Plain disconnected" })
		},
		onError: (error) => {
			toast.error({
				title: "Could not disconnect Plain",
				description: error.message,
			})
		},
	})
	const connectMutation = api.workspace.connectPlain.useMutation({
		onSuccess: async () => {
			await ctx.workspace.info.invalidate()
			toast.success({ title: "Plain connected" })
		},
		onError: (error) => {
			toast.error({
				title: "Could not connect Plain",
				description: error.message,
			})
		},
	})
	const { passes: hasRequiredPlan } = useRequiredPlans(pro.andUp)
	const connected = data.workspace.plainConnected
	const form = useZodForm({
		schema,
	})
	const [dialogOpen, setDialogOpen] = useState(false)

	return (
		<>
			<IntegrationLayout />
			<IntegrationTemplate
				logo={<PlainLogo className="h-8 w-8" />}
				name={
					<div className="flex flex-1 items-center gap-3 font-medium">
						Plain integration
						{connected ? <Badge variant="green">Connected</Badge> : null}
					</div>
				}
				description={`Easily push conversations to Productlane, view related projects in Plain and more.`}
				button={
					<div>
						{hasRequiredPlan && connected && (
							<Button
								isLoading={disconnectMutation.isLoading}
								onClick={() => setDialogOpen(true)}
							>
								Disconnect
							</Button>
						)}
					</div>
				}
				instructions={
					<div className="relative">
						<div className="mb-2 mt-4 text-xl font-semibold text-primary">
							How it works
						</div>
						<div className="text-md mb-2 text-secondary">
							With our Plain integration you can easily push conversations to
							Productlane, view related projects in Plain and more. Simply add
							the &apos;Productlane&apos; label to a conversation in Plain and
							it will be pushed to Productlane.
						</div>
						{!connected && (
							<div className="mb-8 text-secondary">
								To connect, create a machine user, create an API key for that
								machine user with all permissions and paste it here.
							</div>
						)}

						{!connected ? (
							<Form
								form={form}
								onSubmit={(values: { token: string }) => {
									connectMutation.mutate({ apiKey: values.token })
									analytics.track("connect-plain")
								}}
								className="mt-4 flex items-start gap-4"
							>
								<FormInput
									name="token"
									placeholder="plainApiKey_uKjL..."
									className="flex-1"
									type="password"
								/>
								{hasRequiredPlan ? (
									<SubmitButton isLoading={connectMutation.isLoading}>
										Connect
									</SubmitButton>
								) : (
									<UpgradeButton
										required={["pro", "scale", "business", "enterprise"]}
									/>
								)}
							</Form>
						) : null}
					</div>
				}
			/>

			<AlertDialog
				variant="positive"
				isOpen={dialogOpen}
				onOpenChange={setDialogOpen}
				confirmText="Disconnect"
				title="Disconnect Plain Integration?"
				description="Are you sure you want to disconnect Plain? Your data will remain saved, but the integration will be disabled."
				onCancel={() => {
					setDialogOpen(false)
				}}
				onConfirm={() => {
					analytics.track("disconnect-plain")
					disconnectMutation.mutate()
					setDialogOpen(false)
				}}
			/>
		</>
	)
}
